import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, CalendarDays, Sparkles, Sprout } from "lucide-react";
import { SERVICES, SEASON_META, ICONS } from "@/lib/services";

const EASE = [0.22, 1, 0.36, 1];

function ServiceCard({ service, index, inSeason, onBook }) {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[service.icon] || Sprout;
  const spanClass = index < 3 ? "md:col-span-2" : "md:col-span-3";

  return (
    <motion.article
      data-testid={`service-card-${service.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: EASE }}
      className={`group relative bg-white rounded-3xl border border-emerald-950/10 shadow-xl shadow-emerald-950/5 overflow-hidden flex flex-col ${spanClass}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/50 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 w-10 h-10 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center text-pine shadow-md">
          <Icon size={19} strokeWidth={2.2} />
        </div>
        {inSeason && (
          <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold uppercase tracking-widest bg-limed text-pine-dark px-2.5 py-1 rounded-full">
            <Sparkles size={11} />
            In season
          </span>
        )}
        <span className="absolute bottom-3 left-4 font-display font-bold text-bone text-lg drop-shadow-md">
          {service.price}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-xl font-bold tracking-tight text-emerald-950">{service.name}</h3>
        <p className="mt-2 text-sm text-emerald-900/70 leading-relaxed flex-1">{service.blurb}</p>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="overflow-hidden"
            >
              <p className="mt-3 text-sm text-emerald-900/80 leading-relaxed border-l-2 border-limed pl-3">
                {service.details}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            data-testid={`service-expand-btn-${service.id}`}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold uppercase tracking-widest text-emerald-900/50 hover:text-pine transition-colors duration-200"
          >
            {open ? "Hide details" : "Details"}
            <ChevronDown size={13} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          <button
            data-testid={`service-book-now-btn-${service.id}`}
            onClick={() => onBook(service.name)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-pine hover:text-limed transition-colors duration-200"
          >
            <CalendarDays size={15} strokeWidth={2.5} />
            Schedule this
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Services({ season, onBook, services = SERVICES }) {
  const inSeasonIds = SEASON_META[season].services;
  return (
    <section id="services" className="py-24 bg-bone">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] font-mono font-semibold text-limed mb-4">Our Services</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-emerald-950 leading-[1.08]">
            Whatever the season,
            <span className="text-outline block">we've got the crew.</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-emerald-900/70 leading-relaxed">
            Affordable services to help keep your property looking its best throughout the year.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {services.map((s, i) => (
            <ServiceCard
              key={s.id}
              service={s}
              index={i}
              inSeason={inSeasonIds.includes(s.id)}
              onBook={onBook}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
